import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {Divider, Grid} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect} from 'react';

import {ANTIBODY_REST_PROPS_BY_ID} from 'antibodies/AntibodyConstants';
import {useAntibodyFormatsQuery} from 'antibodies/hooks/useAntibodyFormatsQuery';
import AntibodyComponentSvg, {
  AntibodyComponentType,
} from 'antibodies/nested-data-creation/antibody-component-visualization/AntibodyComponentSvg';
import {
  ALLOWED_ANTIBODY_FORMAT_IDS,
  NestedDataType,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {RegistryIds} from 'antibodies/nested-data-creation/panes/__generated/RegistryIds';
import AntibodyFormatPicker from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import RegistrySelector from 'registry/RegistrySelector';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import {AntibodyFormatGraphQL} from '../../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

interface InputOutputComponentButtonProps {
  componentType: AntibodyComponentType;
  componentName: string;
  isSelected: boolean;
}

function InputOutputComponentButton({
  componentType,
  componentName,
  isSelected,
}: InputOutputComponentButtonProps): JSX.Element {
  return (
    <div className={css(styles.buttonWrapper)}>
      <button
        className={classNames(css(styles.button), {
          [css(styles.selectedButton)]: isSelected,
        })}
        onClick={noop}
        type="button"
        aria-label={`${componentName} option`}
      >
        <AntibodyComponentSvg componentType={componentType} size={100} />
      </button>
      <div className={css(styles.buttonLabel)}>{componentName}</div>
    </div>
  );
}

export const REGISTRY_IDS_QUERY = gql`
  query RegistryIds @skipAudit @engComponent(name: REGISTRY) {
    registries {
      ... on RegistryNode {
        id
      }
    }
  }
`;

/**
 * Nested data creation modal step that allows the user to select the format of the antibodies they want to create.
 *
 * Current limitations:
 * - Only supports selecting from antibody formats that involve a single variable pair (VH/VL).
 * - The input and output components are not configurable by the user: the input is always DNA domain
 *   and both AA domains and AA chains must always be created as outputs.
 */
export interface SelectFormatPaneProps {
  selectedRegistryId: string | null;
  setSelectedRegistryId: (registryId: string | null) => void;
  selectedFormatId: string | null;
  setSelectedFormat: (format: AntibodyFormatGraphQL | null) => void;
  startingDataType: NestedDataType;
  outputTypes: ReadonlySet<NestedDataType>;
}

export default function SelectFormatPane({
  selectedRegistryId,
  setSelectedRegistryId,
  selectedFormatId,
  setSelectedFormat,
  startingDataType,
  outputTypes,
}: SelectFormatPaneProps): JSX.Element {
  const antibodyFormats = useAntibodyFormatsQuery();
  const result = useBenchlingQuery<RegistryIds>(REGISTRY_IDS_QUERY);

  useEffect(() => {
    // If there is only one registry, select it by default. RegistrySelector would do this itself, but we
    // don't render RegistrySelector if there is only one registry, so we need to do it here.
    if (
      result.type === QueryResultType.SUCCESS &&
      result.data.registries.length === 1 &&
      selectedRegistryId !== result.data.registries[0].id
    ) {
      setSelectedRegistryId(result.data.registries[0].id);
    }
  }, [result, setSelectedRegistryId, selectedRegistryId]);

  const antibodyProps = antibodyFormats
    .filter(({id}) => ALLOWED_ANTIBODY_FORMAT_IDS.includes(id))
    .map((format) => ({
      format,
      showLabels: false,
      ...ANTIBODY_REST_PROPS_BY_ID[format.id],
    }));

  function onSelectFormatId(formatId: string | null): void {
    const format = formatId != null ? antibodyFormats.find(({id}) => id === formatId) : null;
    setSelectedFormat(format ?? null);
  }

  return (
    <div>
      {result.type === QueryResultType.PENDING ? (
        <HorizontalThrobber />
      ) : result.type === QueryResultType.SUCCESS && result.data.registries.length !== 1 ? (
        <>
          <h3>Registry</h3>
          <RegistrySelector
            onChange={({target: {value: registry}}) => {
              setSelectedRegistryId(registry?.id ?? null);
            }}
            registryId={selectedRegistryId}
          />
        </>
      ) : null}

      <h3>Format</h3>
      <p>Select the format of the antibodies you want to create</p>
      <AntibodyFormatPicker
        antibodyProps={antibodyProps}
        disabled={false}
        selectedFormat={selectedFormatId}
        setSelectedFormat={onSelectFormatId}
      />

      <Divider my="md" />

      <h3>Nested components</h3>
      <p>
        Select the input component and any additional output components you want to generate alongside the
        antibodies
      </p>

      <Grid>
        <Grid.Col span={4} {...getTestProps({component: 'SelectFormatPane', element: 'inputsSection'})}>
          <h5>INPUT COMPONENT</h5>
          <div className={css(styles.inputOutputSection)}>
            <InputOutputComponentButton
              componentType={AntibodyComponentType.DNA_DOMAIN}
              componentName="DNA domain"
              isSelected={startingDataType === NestedDataType.DNA_DOMAIN}
            />
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_DOMAIN}
              componentName="AA domain"
              isSelected={startingDataType === NestedDataType.AA_DOMAIN}
            />
          </div>
        </Grid.Col>

        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'outputsSection'})}>
          <h5>OUTPUT COMPONENTS</h5>
          <div className={css(styles.inputOutputSection)}>
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_DOMAIN}
              componentName="AA domain"
              isSelected={outputTypes.has(NestedDataType.AA_DOMAIN)}
            />
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_CHAIN}
              componentName="AA chain"
              isSelected={outputTypes.has(NestedDataType.AA_CHAIN)}
            />
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
}

const styles = StyleSheet.create({
  buttonWrapper: {
    width: 'max-content',
  },
  button: {
    border: 'none',
    borderRadius: '4px',
  },
  selectedButton: {
    border: `1px solid ${Color.BLUE_450}`,
  },
  buttonLabel: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    display: 'flex',
    justifyContent: 'center',
    marginTop: '4px',
  },
  inputOutputSection: {
    display: 'flex',
    gap: '16px',
  },
});
